// Fixed flags also used outside React; these do not have browser overrides.
export const MANUAL_FEATURE_FLAGS = {
  HELP_ENABLED: false,
  ALLOW_DELETE_FIELD: true,
  ALLOW_MAGIC_WAND: true,
  ENABLE_REPLACE_STATION: true,
  ENABLE_SUSPEND_STATION: false,
  FEATURE_NETWORK_ACC_RAIN: true,
  FEATURE_NETWORK_WIND: true,
  FEATURE_NETWORK_AIR_TEMP_24H: true,
  FEATURE_NETWORK_SOIL_TEMP_24H: true,
  FEATURE_WEATHER_HISTORY: false,
  DA_SOILOPTIX_SURVEYS: true,
  LEGACY_SOIL_SAMPLES: false,
  ALLOW_GRAPH_X_AXIS_CLICK: false,
  MARQUEE_SELECT: true,
  ADD_FIELDS_DRAW: true,
  ADD_FIELDS_SHAPEFILE: true,
  SINGLE_FIELD_ANALYSIS: true,
  PIN_LAYER_DRAWERS: true,
  BULK_WEATHER_DOWNLOAD: true,
  OVERRIDE_CLAIMED_SINCE: false,
  PRESCRIPTION_BUFFER_ZONE: true,
  PRESCRIPTION_MINIATURES: true,
  DRAW_IMAGES_MANUALLY: false,
  KORTFORSYNINGEN_HEIGHTMAP: false,
  NOTE_COLLISION_LIST: true,
  ENABLE_WEATHER_MAKEUP: true,
  ENABLED_FETCH_SURVEY_STATISTICS: false,
} as const;

export const NEW_SEEDING_MODEL_FLAG = 'newSeedingModel';

// InDevelopment enables the feature when running npm start. ReadyForTesting enables it on 'dev' while
// 'released' enables it on all environments.
export type FeatureFlagStatus = 'inDevelopment' | 'readyForTesting' | 'released';

export const FEATURE_FLAGS = {
  [NEW_SEEDING_MODEL_FLAG]: {
    label: 'New seeding model',
    status: 'readyForTesting',
  },
} as const satisfies Record<string, { label: string; status: FeatureFlagStatus }>;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;
export type FeatureFlagOverrides = Partial<Record<FeatureFlag, boolean>>;

export const FEATURE_FLAG_STORAGE_KEY = 'featureFlagOverrides';

function getFeatureFlagStatus(flag: FeatureFlag): FeatureFlagStatus {
  return FEATURE_FLAGS[flag].status;
}

export function unreleasedFeatureFlags(): FeatureFlag[] {
  return (Object.keys(FEATURE_FLAGS) as FeatureFlag[]).filter(
    (flag) => getFeatureFlagStatus(flag) !== 'released'
  );
}

export function featureFlagMenuAvailable(
  deploymentEnvironment: string | undefined = process.env.REACT_APP_DEPLOY_ENV,
  nodeEnvironment: string | undefined = process.env.NODE_ENV
): boolean {
  if (unreleasedFeatureFlags().length === 0) return false;
  return (
    nodeEnvironment === 'development' && (!deploymentEnvironment || deploymentEnvironment === 'dev')
  );
}

export function defaultFeatureFlagEnabled(
  flag: FeatureFlag,
  deploymentEnvironment: string | undefined = process.env.REACT_APP_DEPLOY_ENV,
  nodeEnvironment: string | undefined = process.env.NODE_ENV
): boolean {
  return featureFlagEnabledByDefault(
    FEATURE_FLAGS[flag].status,
    deploymentEnvironment,
    nodeEnvironment
  );
}

export function featureFlagEnabledByDefault(
  status: FeatureFlagStatus,
  deploymentEnvironment: string | undefined = process.env.REACT_APP_DEPLOY_ENV,
  nodeEnvironment: string | undefined = process.env.NODE_ENV
): boolean {
  if (status === 'released') return true;
  if (nodeEnvironment === 'development') return true;
  return (
    status === 'readyForTesting' &&
    (deploymentEnvironment === 'dev' || deploymentEnvironment === 'staging')
  );
}

export function isFeatureFlagEnabled(
  status: FeatureFlagStatus,
  override: boolean | undefined,
  deploymentEnvironment: string | undefined = process.env.REACT_APP_DEPLOY_ENV,
  nodeEnvironment: string | undefined = process.env.NODE_ENV
): boolean {
  if (status === 'released') return true;
  return typeof override === 'boolean'
    ? override
    : featureFlagEnabledByDefault(status, deploymentEnvironment, nodeEnvironment);
}

export function readFeatureFlagOverrides(storage: Storage): FeatureFlagOverrides {
  try {
    const value = storage.getItem(FEATURE_FLAG_STORAGE_KEY);
    if (!value) return {};

    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const overrides: FeatureFlagOverrides = {};
    const savedValues = parsed as Record<string, unknown>;
    for (const flag of Object.keys(FEATURE_FLAGS) as FeatureFlag[]) {
      const savedValue = savedValues[flag];
      if (typeof savedValue === 'boolean') {
        overrides[flag] = savedValue;
      }
    }
    return overrides;
  } catch (error) {
    console.error('Could not read feature flag choices', error);
    return {};
  }
}
